/**
 * Los valores seleccionables de los desplegables de CA y estado MITECO.
 *
 * El contrato no tiene endpoint de opciones, y el `CLAUDE.md` dice que el vocabulario lo sirve el
 * backend y no se hardcodea en la UI. Los desgloses de un preview son exactamente los valores que
 * existen en ese conjunto, así que sirven de vocabulario sin endpoint nuevo.
 *
 * EL CALLEJÓN SIN SALIDA QUE ESTO ARREGLA (encontrado por Javi, 2026-08-11): antes solo se rellenaba
 * con un preview SIN filtros. Si alguien empezaba filtrando por fecha, ese caso no entraba nunca, la
 * lista de CAs se quedaba vacía, y no había forma de salir salvo limpiar todos los filtros y volver a
 * empezar — sin que nada explicara por qué.
 *
 * Ahora se ACUMULA de cualquier preview, y se distingue si la lista está completa:
 * - Un preview sin filtros ve todo el universo visible, así que su vocabulario ES el completo.
 * - Un preview filtrado solo ve su subconjunto. Sus valores se añaden —mejor poder elegir algo que
 *   nada— pero la lista queda marcada como PARCIAL, porque una lista incompleta que parece completa
 *   haría creer a Ops que no existen más comunidades que las que ve.
 *
 * Nunca se quitan valores: solo se añaden. Así un preview estrecho no puede reducir lo que ya se
 * sabía que existe.
 */

export interface Vocabulario {
  regiones: string[];
  miteco: string[];
  /**
   * Ficheros/remesas de certificación MITECO (2026-09-08). A diferencia de `regiones`/`miteco`,
   * el centinela `'No Certificados MITECO'` que pone dbt para los viajes aún no certificados SÍ es
   * una opción seleccionable más — no es un bucket vacío como `null` en `regiones`, es un string
   * real que el 99,98% de los viajes visibles llevan hoy.
   */
  ficheros: string[];
  periodos: string[];
  /** Motivos de soft killer (BB-2004). Solo los rellena el programa `softKiller`. */
  motivos: string[];
  /** `true` solo si en algún momento hubo un preview sin filtros. */
  completo: boolean;
}

export const VOCABULARIO_VACIO: Vocabulario = {
  regiones: [],
  miteco: [],
  ficheros: [],
  periodos: [],
  motivos: [],
  completo: false,
};

function unir(previos: string[], nuevos: readonly (string | null)[]): string[] {
  const set = new Set(previos);
  for (const v of nuevos) {
    if (v !== null && v !== '') set.add(v);
  }
  // `localeCompare` con 'es' para que el orden sea el que espera quien lee, no el de los puntos de
  // código: sin él, una lista con acentos se ordena de forma que parece aleatoria.
  return [...set].sort((a, b) => a.localeCompare(b, 'es'));
}

export interface DesglosesDelPreview {
  regiones: readonly (string | null)[];
  miteco: readonly (string | null)[];
  ficheros: readonly (string | null)[];
  periodos: readonly (string | null)[];
  motivos: readonly (string | null)[];
}

export function acumularVocabulario(
  previo: Vocabulario,
  desgloses: DesglosesDelPreview,
  previewSinFiltros: boolean,
): Vocabulario {
  return {
    regiones: unir(previo.regiones, desgloses.regiones),
    miteco: unir(previo.miteco, desgloses.miteco),
    ficheros: unir(previo.ficheros, desgloses.ficheros),
    periodos: unir(previo.periodos, desgloses.periodos),
    motivos: unir(previo.motivos, desgloses.motivos),
    // Una vez completo, sigue completo: un preview filtrado posterior no lo vuelve parcial, porque
    // los valores que ya se conocen no desaparecen.
    completo: previo.completo || previewSinFiltros,
  };
}
